import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { CashAppAccount, DebitCard } from 'utils/payments';

import type { ServerError } from '../../models';
import type {
    CreateApplePayMerchantSessionRequest,
    CreateApplePayMerchantSessionResponse,
    CreateStripeCryptoWithdrawalRequest,
    CreateStripeCryptoWithdrawalResponse,
    CreateStripeWithdrawalRequest,
    CreateStripeWithdrawalResponse,
    CreateWithdrawalResponse,
    DebitCardPaymentSession,
    DebitCardPaymentSessionRequest,
    DebitCardWithdrawalRequest,
    GetBraintreeClientTokenResponse,
    GetBraintreePaymentMethodsResponse,
    GetCashAppAccountsResponse,
    GetDebitCardsResponse,
    GetDebitPaymentStatusResponse,
    GetPaymentsMetadataResponse,
    InitiateBraintreeDepositRequest,
    InitiateBraintreeDepositResponse,
    InitiateBraintreeWithdrawalRequest,
    InitiateBraintreeWithdrawalResponse,
    ListStripeApplePayPayoutMethodsResponse,
    ListStripeCardPayoutMethodsResponse,
    ListStripeCryptoPayoutMethodsResponse,
    RegisterStripeApplePayPayoutMethodRequest,
    RegisterStripeApplePayPayoutMethodResponse,
    SavedDebitCardDepositRequest,
    SavedDebitCardDepositResponse,
    StripeANIDepositRequest,
    StripeANIDepositResponse,
    StripeAccountLinkResponse,
    StripePaymentIntent,
    StripePaymentIntentRequest,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function startDebitCardPaymentSession(
    userId: string,
    payload: DebitCardPaymentSessionRequest,
    onSuc: (paymentSession: DebitCardPaymentSession) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<DebitCardPaymentSession>(`/users/${userId}/debit/payment_session`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripePaymentIntent(
    userId: string,
    payload: StripePaymentIntentRequest,
    onSuc: (paymentIntent: StripePaymentIntent) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StripePaymentIntent>(`/users/${userId}/stripe/payment_intent`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripeCashappPaymentIntent(
    userId: string,
    payload: StripePaymentIntentRequest,
    onSuc: (paymentIntent: StripePaymentIntent) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StripePaymentIntent>(`/users/${userId}/stripe/cashapp_payment_intent`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripeCryptoPaymentIntent(
    userId: string,
    payload: StripePaymentIntentRequest,
    onSuc: (paymentIntent: StripePaymentIntent) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StripePaymentIntent>(`/users/${userId}/stripe/crypto_payment_intent`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getCashAppAccount(
    userId: string,
    onSuc: (cashAppAccount: CashAppAccount) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<CashAppAccount>(`/users/${userId}/stripe/cashapp_account`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deleteCashAppAccount(
    userId: string,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete<GetCashAppAccountsResponse>(`/users/${userId}/stripe/cashapp_account`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripeANIDeposit(
    userId: string,
    payload: StripeANIDepositRequest,
    onSuc: (response: StripeANIDepositResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StripeANIDepositResponse>(`/users/${userId}/stripe/ani_deposit`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getStripeAccountLink(
    userId: string,
    returnUrl: string,
    refreshUrl: string,
    onSuc: (response: StripeAccountLinkResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<StripeAccountLinkResponse>(
            `/users/${userId}/stripe/account_link`,
            {
                return_url: returnUrl,
                refresh_url: refreshUrl,
            },
            {
                cancelToken: cancelRef.token,
            },
        )
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripeWithdrawal(
    userId: string,
    payload: CreateStripeWithdrawalRequest,
    onSuc: (response: CreateStripeWithdrawalResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateStripeWithdrawalResponse>(`/users/${userId}/stripe/withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function listStripeCardPayoutMethods(
    userId: string,
    onSuc: (response: ListStripeCardPayoutMethodsResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<ListStripeCardPayoutMethodsResponse>(`/users/${userId}/stripe/card_payout_methods`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function listStripeCryptoPayoutMethods(
    userId: string,
    onSuc: (response: ListStripeCryptoPayoutMethodsResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<ListStripeCryptoPayoutMethodsResponse>(`/users/${userId}/stripe/crypto_payout_methods`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function registerStripeApplePayPayoutMethod(
    userId: string,
    payload: RegisterStripeApplePayPayoutMethodRequest,
    onSuc: (response: RegisterStripeApplePayPayoutMethodResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<RegisterStripeApplePayPayoutMethodResponse>(`/users/${userId}/stripe/apple_pay_payout_method`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function listStripeApplePayPayoutMethods(
    userId: string,
    onSuc: (response: ListStripeApplePayPayoutMethodsResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<ListStripeApplePayPayoutMethodsResponse>(`/users/${userId}/stripe/apple_pay_payout_methods`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createApplePayMerchantSession(
    userId: string,
    payload: CreateApplePayMerchantSessionRequest,
    onSuc: (response: CreateApplePayMerchantSessionResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateApplePayMerchantSessionResponse>(`/users/${userId}/apple_pay/merchant_session`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function createStripeCryptoWithdrawal(
    userId: string,
    payload: CreateStripeCryptoWithdrawalRequest,
    onSuc: (response: CreateStripeCryptoWithdrawalResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateStripeCryptoWithdrawalResponse>(`/users/${userId}/stripe/crypto_withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getBraintreeClientToken(
    userId: string,
    onSuc: (response: GetBraintreeClientTokenResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetBraintreeClientTokenResponse>(`/users/${userId}/braintree/client_token`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getBraintreePaymentMethods(
    userId: string,
    onSuc: (response: GetBraintreePaymentMethodsResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetBraintreePaymentMethodsResponse>(`/users/${userId}/braintree/payment_methods`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function initiateBraintreeDeposit(
    userId: string,
    payload: InitiateBraintreeDepositRequest,
    onSuc: (response: InitiateBraintreeDepositResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<InitiateBraintreeDepositResponse>(`/users/${userId}/braintree/deposit`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deleteBraintreePaymentMethod(
    userId: string,
    type: 'paypal' | 'venmo',
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}/braintree/payment_methods/${type}`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function initiateBraintreeWithdrawal(
    userId: string,
    payload: InitiateBraintreeWithdrawalRequest,
    onSuc: (response: InitiateBraintreeWithdrawalResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<InitiateBraintreeWithdrawalResponse>(`/users/${userId}/braintree/withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getPaymentsMetadata(
    userId: string,
    onSuc: (response: GetPaymentsMetadataResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetPaymentsMetadataResponse>(`/users/${userId}/payments/metadata`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function debitCardWithdrawal(
    userId: string,
    payload: DebitCardWithdrawalRequest,
    onSuc: (response?: CreateWithdrawalResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<CreateWithdrawalResponse>(`/users/${userId}/debit/withdrawal`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function savedDebitCardDeposit(
    userId: string,
    payload: SavedDebitCardDepositRequest,
    onSuc: (response: SavedDebitCardDepositResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post<SavedDebitCardDepositResponse>(`/users/${userId}/debit/payment`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getSavedDebitCards(
    userId: string,
    onSuc: (debitCards: DebitCard[]) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetDebitCardsResponse>(`/users/${userId}/debit/cards`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.debit_cards);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function deleteDebitCard(
    userId: string,
    cardId: string,
    onSuc: () => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete<GetDebitCardsResponse>(`/users/${userId}/debit/cards/${cardId}`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getDebitPaymentStatus(
    userId: string,
    checkoutPaymentId: string,
    onSuc: (title: string, reason: string) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetDebitPaymentStatusResponse>(
            `/users/${userId}/debit/payment_status_web${buildQueryString({
                checkout_payment_id: checkoutPaymentId,
            })}`,
            {
                cancelToken: cancelRef.token,
            },
        )
        .then((response) => {
            onSuc(response.data.title, response.data.reason);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
